import React, { useEffect, useMemo, useRef, useState } from "react";
import { ContentCard } from "../components/ContentCard";
import { useNavigate, useParams } from "react-router-dom";
import {
  useComparablesSummary,
  usePricePredictions,
} from "../hooks/pricePredictions.hooks";
import { useMutation, useQuery } from "react-query";
import { FinalPricePredictions } from "common/types/common.types";
import {
  addPricePrediction,
  createConfirmation,
  getConfidenceMeasureForPricePrediction,
} from "../api/api";
import { MUILoadingButton } from "../components/buttons/MUILoadingButton";
import { ComparablesSummaryTable } from "../components/comparablesSummary/ComparablesSummaryTable";
import { ValuationSummary } from "../components/comparablesSummary/ValuationSummary";
import {
  AdjustmentsJustification,
  AdjustmentsJustificationHandles,
} from "../components/comparablesSummary/AdjustmentsJustification";
import { CompositeAssets } from "../components/comparablesSummary/CompositeAssets";
import { PredictionsConfirmButton } from "./PredictionsConfirmButton";
import {
  ConfirmationForm,
  ConfirmationFormHandles,
} from "../components/comparablesSummary/ConfirmationForm";
import { ConfirmationRequest } from "../types/api.types";
import { showToastError } from "common/toast/toast";
import { useProperty } from "../hooks/property.hooks";
import { getErrorMessage } from "common/helpers/error.helpers";
import { useDeletePropertyToken } from "common/hooks/propertyDetails.hooks";
import { PredictionsConfidenceMeasure } from "./property/PredictionsConfidenceMeasure";
import { LinkForButton } from "../components/links/LinkForButton";
import { Button, TextField } from "@mui/material";
import { AutoPricePrediction } from "../components/predictionOverview/AutoPricePrediction";
import { AppraiserMismatchDialog } from "../components/dialogs/AppraiserMismatchDialog";

interface PropertyValuationSummaryProps {
  children?: React.ReactNode;
  adjustmentsJustificationRef?: React.MutableRefObject<AdjustmentsJustificationHandles | null>;
  ignoreComparables?: boolean;
}

export const PropertyValuationSummary: React.FC<
  PropertyValuationSummaryProps
> = ({ children, adjustmentsJustificationRef, ignoreComparables }) => {
  const { propertyId } = useParams();

  useDeletePropertyToken(propertyId ?? "");

  const { getFinalPricePredictions, pricePredictions, refetch } =
    usePricePredictions(propertyId ?? "");

  const [appraiserComment, setAppraiserComment] = useState<string>("");

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (pricePredictions?.appraiserComment) {
      setAppraiserComment(pricePredictions.appraiserComment);
    }
  }, [pricePredictions?.appraiserComment]);

  const navigate = useNavigate();
  const {
    mutateAsync: mutateAsyncPredictions,
    isLoading: isLoadingPredictions,
  } = useMutation(
    async (pricePrediction: FinalPricePredictions) => {
      await addPricePrediction<FinalPricePredictions>(
        propertyId ?? "",
        pricePrediction,
        true
      );
    },
    {
      onError: (error) => {
        showToastError(getErrorMessage(error));
      },
    }
  );

  const [appraiserMismatchDialogOpen, setAppraiserMismatchDialogOpen] =
    useState(false);
  const skipAppraiserCheckRef = useRef(false);

  const {
    mutateAsync: mutateAsyncConfirmation,
    isLoading: isLoadingConfirmation,
  } = useMutation(
    async (x: {
      data: ConfirmationRequest;
      propertyId: string;
      forceSameAppraiser?: boolean;
    }) => createConfirmation(x.data, x.propertyId, x.forceSameAppraiser ?? true),
    {
      onError: (error: any) => {
        const errorMessage = getErrorMessage(error);
        if (errorMessage === "APPRAISER_MISMATCH") {
          setAppraiserMismatchDialogOpen(true);
          return;
        }
        showToastError(errorMessage);
      },
    }
  );

  const [buttonPressed, setButtonPressed] = React.useState<"save" | "confirm">(
    "save"
  );

  const navigateToProperty = () => {
    navigate(`/properties/${propertyId}`);
  };

  const saveFinalPricePredictions = async (navigateOnSuccess: boolean) => {
    const final = getFinalPricePredictions(ignoreComparables);
    if (!final) return;
    final.descriptions =
      adjustmentsJustificationRef?.current?.getDescriptions();
    // Add appraiser comment to the final price predictions
    final.appraiserComment = appraiserComment;
    await mutateAsyncPredictions(final);

    if (navigateOnSuccess) {
      navigateToProperty();
    }
  };

  const confirmationFormRef = useRef<ConfirmationFormHandles>(null);

  const isLoading = isLoadingConfirmation || isLoadingPredictions;

  const { data: property } = useProperty(propertyId ?? "");

  if (!propertyId) return null;

  const handleContinueWithoutChangingAppraiser = () => {
    setAppraiserMismatchDialogOpen(false);
    skipAppraiserCheckRef.current = true;
    handleConfirmationSubmit();
  };

  const handleAppraiserChanged = () => {
    setAppraiserMismatchDialogOpen(false);
    handleConfirmationSubmit();
  };

  const handleConfirmationSubmit = async () => {
    const fromLead = !!property?.leadContact;

    if (property?.reportVerifiedAt) {
      await saveFinalPricePredictions(!fromLead);
      return true;
    }

    let error = false;
    await confirmationFormRef.current?.handleSubmit(
      async (data) => {
        setButtonPressed(fromLead ? "confirm" : "save");

        const forceSameAppraiser = !skipAppraiserCheckRef.current;
        skipAppraiserCheckRef.current = false;

        await mutateAsyncConfirmation({
          data,
          propertyId,
          forceSameAppraiser,
        });

        await saveFinalPricePredictions(!fromLead);
      },
      (data) => {
        if (data.confirmCheckbox) {
          showToastError("Patvirtinkite, kad sutinkate su sąlygomis");
        }
        error = true;
      }
    )();
    if (error) {
      return false;
    }
    return true;
  };

  const currentAppraiserEmail = property?.appraiser?.email;

  return (
    <div style={{ padding: "15px 0" }}>
      {children}
      <ContentCard title="Priklausomas turtas" borderRadius={0}>
        <CompositeAssets
          propertyId={propertyId}
          pricePredictionId={pricePredictions?._id}
        />
      </ContentCard>

      <ContentCard title="Vertintojo komentaras dėl pataisų">
        <TextField
          label="Komentaras"
          multiline
          rows={4}
          value={appraiserComment}
          onChange={(e) => setAppraiserComment(e.target.value)}
          placeholder="Įveskite komentarą apie skaičiavimus..."
          fullWidth
          margin="normal"
        />
      </ContentCard>

      <ContentCard title="Patvirtinimas">
        <ConfirmationForm
          ref={confirmationFormRef}
          disabled={!!property?.reportVerifiedAt}
          propertyId={propertyId}
        />
      </ContentCard>
      <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
        <MUILoadingButton
          loading={isLoading && buttonPressed === "save"}
          disabled={isLoading}
          onClick={
            property?.leadContact
              ? async () => {
                  setButtonPressed("save");
                  await saveFinalPricePredictions(true);
                }
              : handleConfirmationSubmit
          }
          style={{
            width: "300px",
          }}
          variant="outlined"
        >
          Išsaugoti ir uždaryti
        </MUILoadingButton>
        <PredictionsConfirmButton
          disabled={isLoading}
          loading={isLoading && buttonPressed === "confirm"}
          propertyId={propertyId}
          onClick={handleConfirmationSubmit}
          onConfirm={navigateToProperty}
        />
      </div>

      <AppraiserMismatchDialog
        open={appraiserMismatchDialogOpen}
        onClose={() => setAppraiserMismatchDialogOpen(false)}
        onContinue={handleContinueWithoutChangingAppraiser}
        onAppraiserChanged={handleAppraiserChanged}
        propertyId={propertyId}
        currentAppraiserEmail={currentAppraiserEmail}
        isLoading={isLoadingConfirmation}
      />
    </div>
  );
};
